import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Folder,
  Tag,
  MoreVertical,
  Filter,
  List,
  Upload,
} from "lucide-react";
import billingService from "../../services/billingService";
import subcategoryFilterService from "../../services/subcategoryFilterService";
import { showApiMessage } from "../../utils/messageHandler";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { Badge } from "../../shadcn/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../shadcn/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../shadcn/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../shadcn/ui/dropdown-menu";
import { Separator } from "../../shadcn/ui/separator";
import LoadingSpinner from "../../components/LoadingSpinner";
import spaceManagementService from "../../services/host-apiServices/spaceManagementService";
import logger from "../../utils/logger";


const ServiceCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    code: "",
    type: "END_USER",
  });

  const [subcategoryForm, setSubcategoryForm] = useState({
    name: "",
    code: "",
  });

  // Validation states
  const [categoryErrors, setCategoryErrors] = useState({});
  const [subcategoryErrors, setSubcategoryErrors] = useState({});

  // Filter states
  const [filters, setFilters] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());
  const [filtersLoading, setFiltersLoading] = useState(new Set());
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  const [filterForm, setFilterForm] = useState({ name: "", code: "" });
  const [filterErrors, setFilterErrors] = useState({});

  // Filter option states
  const [options, setOptions] = useState({});
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [optionForm, setOptionForm] = useState({
    name: "",
    code: "",
    value: "",
    sortOrder: 0,
    image: "",
    imageType: "SVG",
  });
  const [optionErrors, setOptionErrors] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Validation functions
  const validateCategoryForm = () => {
    const errors = {};
    if (!categoryForm.name.trim()) {
      errors.name = "Name is required";
    }
    if (!categoryForm.code.trim()) {
      errors.code = "Code is required";
    }
    setCategoryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSubcategoryForm = () => {
    const errors = {};
    if (!subcategoryForm.name.trim()) {
      errors.name = "Name is required";
    }
    if (!subcategoryForm.code.trim()) {
      errors.code = "Code is required";
    }
    setSubcategoryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Filter validation
  const validateFilterForm = () => {
    const errors = {};
    if (!filterForm.name.trim()) errors.name = "Name is required";
    if (!filterForm.code.trim()) errors.code = "Code is required";
    setFilterErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Option validation
  const validateOptionForm = () => {
    const errors = {};
    if (!optionForm.name.trim()) errors.name = "Name is required";
    if (!optionForm.code.trim()) errors.code = "Code is required";
    if (!optionForm.value.trim()) errors.value = "Value is required";
    if (optionForm.sortOrder === "" || optionForm.sortOrder === null) errors.sortOrder = "Sort order is required";
    setOptionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchFiltersForSubcategory = async subCategoryId => {
    try {
      setFiltersLoading(prev => new Set(prev).add(subCategoryId));
      const response = await subcategoryFilterService.getSubcategoryFilters(subCategoryId);
      const filterData = response.data || [];
      setFilters(prev => ({ ...prev, [subCategoryId]: filterData }));
      const opts = {};
      filterData.forEach(filter => {
        if (filter.options) {
          opts[filter.id] = filter.options;
        }
      });
      setOptions(prev => ({ ...prev, ...opts }));
    } catch (error) {
      logger.error("Error fetching filters:", error);
      showApiMessage(error, "fetch subcategory filters");
    } finally {
      setFiltersLoading(prev => {
        const next = new Set(prev);
        next.delete(subCategoryId);
        return next;
      });
    }
  };

  const fetchOptionsForFilter = async filterId => {
    try {
      const response = await subcategoryFilterService.getFilterOptions(filterId);
      const optionData = response.data || [];
      setOptions(prev => ({ ...prev, [filterId]: optionData }));
    } catch (error) {
      logger.error("Error fetching filter options:", error);
      showApiMessage(error, "fetch filter options");
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await billingService.getServiceCategories();
      if (response.data?.data) {
        setCategories(response.data.data);
        // Initialize subcategories object
        const subcats = {};
        response.data.data.forEach(category => {
          subcats[category.id] = [];
        });
        setSubcategories(subcats);
      }
    } catch (error) {
      logger.error("Error fetching categories:", error);
      showApiMessage(error, "fetch service categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async categoryId => {
    try {
      const response = await billingService.getServiceSubcategories(categoryId);
      if (response.data?.data) {
        setSubcategories(prev => ({
          ...prev,
          [categoryId]: response.data.data,
        }));
      }
    } catch (error) {
      logger.error("Error fetching subcategories:", error);
      showApiMessage(error, "fetch subcategories");
    }
  };

  const toggleCategoryExpansion = async categoryId => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
      // Fetch subcategories if not already loaded
      if (!subcategories[categoryId] || subcategories[categoryId].length === 0) {
        await fetchSubcategories(categoryId);
      }
    }
    setExpandedCategories(newExpanded);
  };

  const handleCreateCategory = async () => {
    if (!validateCategoryForm()) {
      return;
    }

    try {
      const response = await billingService.createServiceCategory(categoryForm);
      if (response.data?.data) {
        showApiMessage(response, "create service category");
        setShowCategoryModal(false);
        setCategoryForm({ name: "", code: "", type: "END_USER" });
        setCategoryErrors({});
        fetchCategories();
      }
    } catch (error) {
      logger.error("Error creating category:", error);
      // Handle validation errors from backend
      if (error.response?.data?.details) {
        const backendErrors = {};
        error.response.data.details.forEach(detail => {
          if (detail.includes("name")) backendErrors.name = "Name is required";
          if (detail.includes("code")) backendErrors.code = "Code is required";
        });
        setCategoryErrors(backendErrors);
      }
      showApiMessage(error, "create service category");
    }
  };

  const handleUpdateCategory = async () => {
    if (!validateCategoryForm()) {
      return;
    }

    try {
      const response = await billingService.updateServiceCategory(editingCategory.id, categoryForm);
      if (response.data?.data) {
        showApiMessage(response, "update service category");
        setShowCategoryModal(false);
        setEditingCategory(null);
        setCategoryForm({ name: "", code: "", type: "END_USER" });
        setCategoryErrors({});
        fetchCategories();
      }
    } catch (error) {
      logger.error("Error updating category:", error);
      // Handle validation errors from backend
      if (error.response?.data?.details) {
        const backendErrors = {};
        error.response.data.details.forEach(detail => {
          if (detail.includes("name")) backendErrors.name = "Name is required";
          if (detail.includes("code")) backendErrors.code = "Code is required";
        });
        setCategoryErrors(backendErrors);
      }
      showApiMessage(error, "update service category");
    }
  };

  const handleDeleteCategory = async categoryId => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        const response = await billingService.deleteServiceCategory(categoryId);
        showApiMessage(response, "delete service category");
        fetchCategories();
      } catch (error) {
        logger.error("Error deleting category:", error);
        showApiMessage(error, "delete service category");
      }
    }
  };

  const handleCreateSubcategory = async () => {
    if (!validateSubcategoryForm()) {
      return;
    }

    try {
      const response = await billingService.createServiceSubcategory(selectedCategoryId, subcategoryForm);
      if (response.data?.data) {
        showApiMessage(response, "create service subcategory");
        setShowSubcategoryModal(false);
        setSubcategoryForm({ name: "", code: "" });
        setSubcategoryErrors({});
        fetchSubcategories(selectedCategoryId);
      }
    } catch (error) {
      logger.error("Error creating subcategory:", error);
      // Handle validation errors from backend
      if (error.response?.data?.details) {
        const backendErrors = {};
        error.response.data.details.forEach(detail => {
          if (detail.includes("name")) backendErrors.name = "Name is required";
          if (detail.includes("code")) backendErrors.code = "Code is required";
        });
        setSubcategoryErrors(backendErrors);
      }
      showApiMessage(error, "create service subcategory");
    }
  };

  const handleUpdateSubcategory = async () => {
    if (!validateSubcategoryForm()) {
      return;
    }

    try {
      const response = await billingService.updateServiceSubcategory(
        selectedCategoryId,
        editingSubcategory.id,
        subcategoryForm
      );
      if (response.data?.data) {
        showApiMessage(response, "update service subcategory");
        setShowSubcategoryModal(false);
        setEditingSubcategory(null);
        setSubcategoryForm({ name: "", code: "" });
        setSubcategoryErrors({});
        fetchSubcategories(selectedCategoryId);
      }
    } catch (error) {
      logger.error("Error updating subcategory:", error);
      // Handle validation errors from backend
      if (error.response?.data?.details) {
        const backendErrors = {};
        error.response.data.details.forEach(detail => {
          if (detail.includes("name")) backendErrors.name = "Name is required";
          if (detail.includes("code")) backendErrors.code = "Code is required";
        });
        setSubcategoryErrors(backendErrors);
      }
      showApiMessage(error, "update service subcategory");
    }
  };

  const handleDeleteSubcategory = async (categoryId, subcategoryId) => {
    if (window.confirm("Are you sure you want to delete this subcategory?")) {
      try {
        const response = await billingService.deleteServiceSubcategory(categoryId, subcategoryId);
        showApiMessage(response, "delete service subcategory");
        fetchSubcategories(categoryId);
      } catch (error) {
        logger.error("Error deleting subcategory:", error);
        showApiMessage(error, "delete service subcategory");
      }
    }
  };

  // ========================================
  // FILTER CRUD
  // ========================================
  const handleCreateFilter = async () => {
    if (!validateFilterForm()) return;
    try {
      const response = await subcategoryFilterService.createSubcategoryFilter({
        ...filterForm,
        subCategoryId: selectedSubcategoryId,
      });
      if (response.status === 201 || response.data) {
        showApiMessage(response, "create filter");
        setShowFilterModal(false);
        setFilterForm({ name: "", code: "" });
        setFilterErrors({});
        fetchFiltersForSubcategory(selectedSubcategoryId);
      }
    } catch (error) {
      handleFilterError(error);
    }
  };

  const handleUpdateFilter = async () => {
    if (!validateFilterForm()) return;
    try {
      const response = await subcategoryFilterService.updateSubcategoryFilter(editingFilter.id, {
        name: filterForm.name,
      });
      if (response.status === 200 || response.data) {
        showApiMessage(response, "update filter");
        setShowFilterModal(false);
        setEditingFilter(null);
        setFilterForm({ name: "", code: "" });
        setFilterErrors({});
        fetchFiltersForSubcategory(selectedSubcategoryId);
      }
    } catch (error) {
      handleFilterError(error);
    }
  };

  const handleDeleteFilter = async (subCategoryId, filterId) => {
    if (window.confirm("Are you sure you want to delete this filter and all its options?")) {
      try {
        const response = await subcategoryFilterService.deleteSubcategoryFilter(filterId);
        showApiMessage(response, "delete filter");
        fetchFiltersForSubcategory(subCategoryId);
      } catch (error) {
        logger.error("Error deleting filter:", error);
        showApiMessage(error, "delete filter");
      }
    }
  };

  const handleFilterError = error => {
    logger.error("Error with filter:", error);
    if (error.response?.data?.details) {
      const backendErrors = {};
      error.response.data.details.forEach(detail => {
        if (detail.includes("name")) backendErrors.name = "Name is required";
        if (detail.includes("code")) backendErrors.code = "Code is required";
      });
      setFilterErrors(backendErrors);
    }
    showApiMessage(error, "filter operation");
  };

  const openFilterModal = (subCategoryId, filter = null) => {
    setSelectedSubcategoryId(subCategoryId);
    if (filter) {
      setEditingFilter(filter);
      setFilterForm({ name: filter.name, code: filter.code });
    } else {
      setEditingFilter(null);
      setFilterForm({ name: "", code: "" });
    }
    setFilterErrors({});
    setShowFilterModal(true);
  };

  // ========================================
  // FILTER OPTION CRUD
  // ========================================
  const handleCreateOption = async () => {
    if (!validateOptionForm()) return;
    try {
      const response = await subcategoryFilterService.createFilterOption({
        ...optionForm,
        filterId: selectedFilterId,
        sortOrder: Number(optionForm.sortOrder),
      });
      if (response.status === 201 || response.data) {
        showApiMessage(response, "create filter option");
        setShowOptionModal(false);
        setOptionForm({ name: "", code: "", value: "", sortOrder: 0, image: "", imageType: "SVG" });
        setOptionErrors({});
        fetchOptionsForFilter(selectedFilterId);
        fetchFiltersForSubcategory(selectedSubcategoryId);
      }
    } catch (error) {
      handleOptionError(error);
    }
  };

  const handleUpdateOption = async () => {
    if (!validateOptionForm()) return;
    try {
      const response = await subcategoryFilterService.updateFilterOption(editingOption.id, {
        name: optionForm.name,
        value: optionForm.value,
        sortOrder: Number(optionForm.sortOrder),
        image: optionForm.image,
        imageType: optionForm.imageType,
      });
      if (response.status === 200 || response.data) {
        showApiMessage(response, "update filter option");
        setShowOptionModal(false);
        setEditingOption(null);
        setOptionForm({ name: "", code: "", value: "", sortOrder: 0, image: "", imageType: "SVG" });
        setOptionErrors({});
        fetchOptionsForFilter(selectedFilterId);
      }
    } catch (error) {
      handleOptionError(error);
    }
  };

  const handleDeleteOption = async (filterId, optionId) => {
    if (window.confirm("Are you sure you want to delete this filter option?")) {
      try {
        const response = await subcategoryFilterService.deleteFilterOption(optionId);
        showApiMessage(response, "delete filter option");
        fetchOptionsForFilter(filterId);
      } catch (error) {
        logger.error("Error deleting filter option:", error);
        showApiMessage(error, "delete filter option");
      }
    }
  };

  const handleOptionImageUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const response = await spaceManagementService.uploadMedia([file]);
      if ((response.status === 200 || response.status === 201) && response.data && response.data.length > 0) {
        const imageUrl = response.data[0];
        setOptionForm(prev => ({ ...prev, image: imageUrl, imageType: "URL" }));
        showApiMessage(response, "upload image");
      } else {
        showApiMessage(response, "upload image");
      }
    } catch (error) {
      showApiMessage(error, "upload image");
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const handleOptionError = error => {
    logger.error("Error with filter option:", error);
    if (error.response?.data?.details) {
      const backendErrors = {};
      error.response.data.details.forEach(detail => {
        if (detail.includes("name")) backendErrors.name = "Name is required";
        if (detail.includes("code")) backendErrors.code = "Code is required";
        if (detail.includes("value")) backendErrors.value = "Value is required";
      });
      setOptionErrors(backendErrors);
    }
    showApiMessage(error, "filter option operation");
  };

  const openOptionModal = (subCategoryId, filterId, option = null) => {
    setSelectedSubcategoryId(subCategoryId);
    setSelectedFilterId(filterId);
    if (option) {
      setEditingOption(option);
      setOptionForm({
        name: option.name,
        code: option.code,
        value: option.value,
        sortOrder: option.sortOrder ?? 0,
        image: option.image || "",
        imageType: option.imageType || "SVG",
      });
    } else {
      setEditingOption(null);
      setOptionForm({ name: "", code: "", value: "", sortOrder: 0, image: "", imageType: "SVG" });
    }
    setOptionErrors({});
    setShowOptionModal(true);
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        code: category.code,
        type: category.type,
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: "", code: "", type: "END_USER" });
    }
    setCategoryErrors({});
    setShowCategoryModal(true);
  };

  const openSubcategoryModal = (categoryId, subcategory = null) => {
    setSelectedCategoryId(categoryId);
    if (subcategory) {
      setEditingSubcategory(subcategory);
      setSubcategoryForm({
        name: subcategory.name,
        code: subcategory.code,
      });
    } else {
      setEditingSubcategory(null);
      setSubcategoryForm({ name: "", code: "" });
    }
    setSubcategoryErrors({});
    setShowSubcategoryModal(true);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Service Categories</h1>
          <p className="mt-2 text-gray-600">Manage service categories and subcategories</p>
        </div>
        <LoadingSpinner
          loading={true}
          message="Loading service categories..."
          padding="py-16"
          className="text-blue-600"
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Service Categories</h1>
            <p className="mt-2 text-gray-600">Manage service categories and subcategories</p>
          </div>
          <Button onClick={() => openCategoryModal()} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>
      </div>

      {/* Categories List */}
      <div className="space-y-4">
        {categories.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Categories Found</h3>
              <p className="text-gray-500 mb-4">Get started by creating your first service category.</p>
              <Button onClick={() => openCategoryModal()}>
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </CardContent>
          </Card>
        ) : (
          categories.map(category => (
            <Card key={category.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCategoryExpansion(category.id)}
                      className="p-1 h-auto"
                    >
                      {expandedCategories.has(category.id) ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </Button>
                    <div className="flex items-center gap-2">
                      {expandedCategories.has(category.id) ? (
                        <FolderOpen className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Folder className="w-5 h-5 text-gray-400" />
                      )}
                      <div>
                        <CardTitle className="text-lg">{category.name}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Badge variant="outline">{category.code}</Badge>
                          <Badge variant="secondary">{category.type}</Badge>
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openSubcategoryModal(category.id)}
                      className="hidden sm:flex"
                    >
                      <Tag className="w-4 h-4 mr-2" />
                      Add Subcategory
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openSubcategoryModal(category.id)}>
                          <Tag className="w-4 h-4 mr-2" />
                          Add Subcategory
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openCategoryModal(category)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Category
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteCategory(category.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Category
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>

              {/* Subcategories */}
              {expandedCategories.has(category.id) && (
                <CardContent className="pt-0">
                  <Separator className="mb-4" />
                  {subcategories[category.id]?.length === 0 ? (
                    <div className="text-center py-8">
                      <Tag className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 mb-4">No subcategories found</p>
                      <Button variant="outline" size="sm" onClick={() => openSubcategoryModal(category.id)}>
                        <Tag className="w-4 h-4 mr-2" />
                        Add Subcategory
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {subcategories[category.id]?.map(subcategory => (
                        <Card key={subcategory.id} className="bg-gray-50 overflow-hidden">
                          <CardContent className="p-0">
                            {/* Subcategory header */}
                            <div
                              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                              onClick={() => {
                                const newExpanded = new Set(expandedSubcategories);
                                if (newExpanded.has(subcategory.id)) {
                                  newExpanded.delete(subcategory.id);
                                } else {
                                  newExpanded.add(subcategory.id);
                                  if (!filters[subcategory.id]) {
                                    fetchFiltersForSubcategory(subcategory.id);
                                  }
                                }
                                setExpandedSubcategories(newExpanded);
                              }}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {expandedSubcategories.has(subcategory.id) ? (
                                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                                )}
                                <Filter className="w-4 h-4 text-blue-500 shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900 truncate">{subcategory.name}</h4>
                                  <p className="text-sm text-gray-500">Code: {subcategory.code}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                                <Button variant="ghost" size="sm" onClick={() => openFilterModal(subcategory.id)}>
                                  <Plus className="w-3.5 h-3.5 mr-1" />
                                  <span className="text-xs">Filter</span>
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openSubcategoryModal(category.id, subcategory)}>
                                      <Edit className="w-4 h-4 mr-2" />
                                      Edit Subcategory
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleDeleteSubcategory(category.id, subcategory.id)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete Subcategory
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>

                            {/* Filters section */}
                            {expandedSubcategories.has(subcategory.id) && (
                              <div className="border-t border-gray-200">
                                {filtersLoading.has(subcategory.id) ? (
                                  <div className="p-4 text-center">
                                    <LoadingSpinner loading={true} message="" padding="py-4" />
                                  </div>
                                ) : filters[subcategory.id]?.length === 0 ? (
                                  <div className="p-4 text-center">
                                    <p className="text-sm text-gray-400 mb-2">No filters yet</p>
                                    <Button variant="outline" size="sm" onClick={() => openFilterModal(subcategory.id)}>
                                      <Plus className="w-3.5 h-3.5 mr-1" />
                                      Add Filter
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="p-3 space-y-2">
                                    {filters[subcategory.id]?.map(filter => (
                                      <div
                                        key={filter.id}
                                        className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                                      >
                                        {/* Filter header */}
                                        <div className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                                          <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <List className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                                            <span className="font-medium text-sm text-gray-800 truncate">
                                              {filter.name}
                                            </span>
                                            <Badge variant="outline" className="text-xs">
                                              {filter.code}
                                            </Badge>
                                          </div>
                                          <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 px-2"
                                              onClick={() => openOptionModal(subcategory.id, filter.id)}
                                            >
                                              <Plus className="w-3 h-3 mr-1" />
                                              <span className="text-xs">Option</span>
                                            </Button>
                                            <DropdownMenu>
                                              <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                                  <MoreVertical className="w-3.5 h-3.5" />
                                                </Button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent align="end">
                                                <DropdownMenuItem
                                                  onClick={() => openFilterModal(subcategory.id, filter)}
                                                >
                                                  <Edit className="w-3.5 h-3.5 mr-2" />
                                                  Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                  onClick={() => handleDeleteFilter(subcategory.id, filter.id)}
                                                  className="text-red-600"
                                                >
                                                  <Trash2 className="w-3.5 h-3.5 mr-2" />
                                                  Delete
                                                </DropdownMenuItem>
                                              </DropdownMenuContent>
                                            </DropdownMenu>
                                          </div>
                                        </div>

                                        {/* Filter options */}
                                        {options[filter.id]?.length > 0 && (
                                          <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                                              {options[filter.id]?.map(option => (
                                                <div
                                                  key={option.id}
                                                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-white border border-gray-100 text-xs"
                                                >
                                                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                                    {option.sortOrder != null && (
                                                      <Badge
                                                        variant="outline"
                                                        className="text-[10px] px-1 py-0 h-4 shrink-0"
                                                      >
                                                        #{option.sortOrder}
                                                      </Badge>
                                                    )}
                                                    {option.image && (
                                                      <img
                                                        src={
                                                          option.imageType === "URL"
                                                            ? option.image
                                                            : `data:image/svg+xml;utf8,${encodeURIComponent(option.image)}`
                                                        }
                                                        alt=""
                                                        className="w-4 h-4 object-contain shrink-0"
                                                        onError={e => {
                                                          e.target.style.display = "none";
                                                        }}
                                                      />
                                                    )}
                                                    <span className="font-medium text-gray-700 truncate">
                                                      {option.name}
                                                    </span>
                                                    <span className="text-gray-400 truncate">: {option.value}</span>
                                                  </div>
                                                  <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                                                        <MoreVertical className="w-2.5 h-2.5" />
                                                      </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="min-w-[120px]">
                                                      <DropdownMenuItem
                                                        onClick={() =>
                                                          openOptionModal(subcategory.id, filter.id, option)
                                                        }
                                                      >
                                                        <Edit className="w-3 h-3 mr-2" />
                                                        Edit
                                                      </DropdownMenuItem>
                                                      <DropdownMenuItem
                                                        onClick={() => handleDeleteOption(filter.id, option.id)}
                                                        className="text-red-600"
                                                      >
                                                        <Trash2 className="w-3 h-3 mr-2" />
                                                        Delete
                                                      </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                  </DropdownMenu>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* No options state */}
                                        {(!options[filter.id] || options[filter.id].length === 0) && (
                                          <div
                                            className="border-t border-gray-100 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                                            onClick={() => openOptionModal(subcategory.id, filter.id)}
                                          >
                                            <p className="text-xs text-gray-400 flex items-center gap-1">
                                              <Plus className="w-3 h-3" />
                                              Add filter option
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit Category" : "Add Category"}</DialogTitle>
            <DialogDescription>
              {editingCategory ? "Update the category details." : "Create a new service category."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="categoryName">Name *</Label>
              <Input
                id="categoryName"
                type="text"
                value={categoryForm.name}
                onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className={categoryErrors.name ? "border-red-500" : ""}
                placeholder="Enter category name"
              />
              {categoryErrors.name && <p className="text-sm text-red-600">{categoryErrors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="categoryCode">Code *</Label>
              <Input
                id="categoryCode"
                type="text"
                value={categoryForm.code}
                onChange={e => setCategoryForm({ ...categoryForm, code: e.target.value })}
                className={categoryErrors.code ? "border-red-500" : ""}
                placeholder="Enter category code"
              />
              {categoryErrors.code && <p className="text-sm text-red-600">{categoryErrors.code}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="categoryType">Type *</Label>
              <Select
                value={categoryForm.type}
                onValueChange={value => setCategoryForm({ ...categoryForm, type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="END_USER">End User</SelectItem>
                  <SelectItem value="HOST">Host</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>
              Cancel
            </Button>
            <Button onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}>
              {editingCategory ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Modal */}
      <Dialog open={showSubcategoryModal} onOpenChange={setShowSubcategoryModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSubcategory ? "Edit Subcategory" : "Add Subcategory"}</DialogTitle>
            <DialogDescription>
              {editingSubcategory ? "Update the subcategory details." : "Create a new subcategory for this category."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subcategoryName">Name *</Label>
              <Input
                id="subcategoryName"
                type="text"
                value={subcategoryForm.name}
                onChange={e =>
                  setSubcategoryForm({
                    ...subcategoryForm,
                    name: e.target.value,
                  })
                }
                className={subcategoryErrors.name ? "border-red-500" : ""}
                placeholder="Enter subcategory name"
              />
              {subcategoryErrors.name && <p className="text-sm text-red-600">{subcategoryErrors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="subcategoryCode">Code *</Label>
              <Input
                id="subcategoryCode"
                type="text"
                value={subcategoryForm.code}
                onChange={e =>
                  setSubcategoryForm({
                    ...subcategoryForm,
                    code: e.target.value,
                  })
                }
                className={subcategoryErrors.code ? "border-red-500" : ""}
                placeholder="Enter subcategory code"
              />
              {subcategoryErrors.code && <p className="text-sm text-red-600">{subcategoryErrors.code}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubcategoryModal(false)}>
              Cancel
            </Button>
            <Button onClick={editingSubcategory ? handleUpdateSubcategory : handleCreateSubcategory}>
              {editingSubcategory ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingFilter ? "Edit Filter" : "Add Filter"}</DialogTitle>
            <DialogDescription>
              {editingFilter ? "Update the filter details." : "Create a new filter for this subcategory."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="filterName">Name *</Label>
              <Input
                id="filterName"
                type="text"
                value={filterForm.name}
                onChange={e => setFilterForm({ ...filterForm, name: e.target.value })}
                className={filterErrors.name ? "border-red-500" : ""}
                placeholder="Enter filter name"
              />
              {filterErrors.name && <p className="text-sm text-red-600">{filterErrors.name}</p>}
            </div>
            {!editingFilter && (
              <div className="space-y-2">
                <Label htmlFor="filterCode">Code *</Label>
                <Input
                  id="filterCode"
                  type="text"
                  value={filterForm.code}
                  onChange={e => setFilterForm({ ...filterForm, code: e.target.value })}
                  className={filterErrors.code ? "border-red-500" : ""}
                  placeholder="Enter filter code"
                />
                {filterErrors.code && <p className="text-sm text-red-600">{filterErrors.code}</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFilterModal(false)}>
              Cancel
            </Button>
            <Button onClick={editingFilter ? handleUpdateFilter : handleCreateFilter}>
              {editingFilter ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filter Option Modal */}
      <Dialog open={showOptionModal} onOpenChange={setShowOptionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingOption ? "Edit Filter Option" : "Add Filter Option"}</DialogTitle>
            <DialogDescription>
              {editingOption ? "Update the filter option details." : "Create a new option for this filter."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-hidden">
            <div className="space-y-2">
              <Label htmlFor="optionName">Name *</Label>
              <Input
                id="optionName"
                type="text"
                value={optionForm.name}
                onChange={e => setOptionForm({ ...optionForm, name: e.target.value })}
                className={optionErrors.name ? "border-red-500" : ""}
                placeholder="Enter option name"
              />
              {optionErrors.name && <p className="text-sm text-red-600">{optionErrors.name}</p>}
            </div>
            {!editingOption && (
              <div className="space-y-2">
                <Label htmlFor="optionCode">Code *</Label>
                <Input
                  id="optionCode"
                  type="text"
                  value={optionForm.code}
                  onChange={e => setOptionForm({ ...optionForm, code: e.target.value })}
                  className={optionErrors.code ? "border-red-500" : ""}
                  placeholder="Enter option code"
                />
                {optionErrors.code && <p className="text-sm text-red-600">{optionErrors.code}</p>}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="optionValue">Value *</Label>
              <Input
                id="optionValue"
                type="text"
                value={optionForm.value}
                onChange={e => setOptionForm({ ...optionForm, value: e.target.value })}
                className={optionErrors.value ? "border-red-500" : ""}
                placeholder="Enter option value"
              />
              {optionErrors.value && <p className="text-sm text-red-600">{optionErrors.value}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="optionSortOrder">Sort Order *</Label>
              <Input
                id="optionSortOrder"
                type="number"
                value={optionForm.sortOrder}
                onChange={e => setOptionForm({ ...optionForm, sortOrder: e.target.value })}
                className={optionErrors.sortOrder ? "border-red-500" : ""}
                placeholder="0"
              />
              {optionErrors.sortOrder && <p className="text-sm text-red-600">{optionErrors.sortOrder}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="optionImage">Image URL</Label>
              <div className="flex gap-2">
                <Input
                  id="optionImage"
                  type="text"
                  value={optionForm.image}
                  onChange={e => setOptionForm({ ...optionForm, image: e.target.value })}
                  placeholder="https://..."
                  className="flex-1"
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={imageInputRef}
                  onChange={handleOptionImageUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {optionForm.image && (
              <div className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 min-w-0">
                <img
                  src={
                    optionForm.imageType === "URL"
                      ? optionForm.image
                      : `data:image/svg+xml;utf8,${encodeURIComponent(optionForm.image)}`
                  }
                  alt="Preview"
                  className="w-10 h-10 rounded object-contain bg-gray-50 shrink-0"
                  onError={e => {
                    e.target.style.display = "none";
                  }}
                />
                <span className="text-xs text-gray-500 truncate min-w-0">
                  {optionForm.imageType === "URL" ? optionForm.image : "SVG image"}
                </span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="optionImageType">Image Type</Label>
              <Select
                value={optionForm.imageType}
                onValueChange={value => setOptionForm({ ...optionForm, imageType: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select image type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SVG">SVG</SelectItem>
                  <SelectItem value="URL">URL</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOptionModal(false)}>
              Cancel
            </Button>
            <Button onClick={editingOption ? handleUpdateOption : handleCreateOption}>
              {editingOption ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceCategoriesPage;
